import type { components } from "@/api-schema";
import { RelocationOrderStatus, RelocationOrderTransportMethod } from "@/api-schema-enums";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { DeviceType } from "@/common/hooks/use-device";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { FitUserType } from "@/components/relocation-order/_helpers/get-status-config";
import { renderPickupDate } from "@/components/relocation-order/relocation-order-pickup-date-renderer";
import { RelocationOrderStatusContainerVariant } from "@/components/relocation-order/relocation-order-status-container";
import { renderRelocationOrderStatus } from "@/components/relocation-order/relocation-order-status-renderer";
import { RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS } from "@/components/relocation-order/relocation-order-transport-reason-translations";
import { EMPTY_VALUE_PLACEHOLDER, EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VEHICLE_BLOCK_REASONS } from "@/components/vehicle-block/_helpers/vehicle-block-reasons";
import i18next from "i18next";
import { LockIcon } from "lucide-react";
import type { ReactElement } from "react";
import { automaticCellRenderer } from "../_helpers/cell-renderer";
import { getRelocationOrderRoutePoints } from "./_helpers/relocation-order-route-points";
import { getRelocationOrderTransportMethodLabel } from "./_helpers/relocation-order-transport-method-label";
import { renderRelocationOrderRoutePoint } from "./_helpers/render-relocation-order-route-point";

type RelocationOrder = components["schemas"]["RelocationOrder"];
type RowData = {
  original: RelocationOrder;
};

const ROUTE_ADDRESS_FIELDS = ["company", "street", "houseNumber", "postalCode", "city"];
const ROUTE_EXPORT_PATHS = ["originAddress", "destinationAddress"].flatMap((address) =>
  ROUTE_ADDRESS_FIELDS.map((field) => `${address}.${field}`),
);
const ROUTE_WITH_STATUS_EXPORT_PATHS = [...ROUTE_EXPORT_PATHS, "status"];
const VEHICLE_BRAND_EXPORT_PATH = "vehicle.modelGeneration.modelFamily.brand.name";
const VEHICLE_MODEL_GENERATION_EXPORT_PATH = "vehicle.modelGeneration.name";
const VEHICLE_VIN_EXPORT_PATH = "vehicle.vin";
const VEHICLE_COLOR_EXPORT_PATH = "vehicle.color";
const VEHICLE_DETAIL_EXPORT_PATHS = [
  "vehicle.modelGeneration.modelFamily.name",
  "vehicle.modelDescription",
  "vehicle.licensePlate",
  "vehicle.status",
  "vehicle.mileage",
  "vehicle.registrationDate",
];
const VEHICLE_EXPORT_PATHS = [
  VEHICLE_BRAND_EXPORT_PATH,
  VEHICLE_MODEL_GENERATION_EXPORT_PATH,
  VEHICLE_VIN_EXPORT_PATH,
  VEHICLE_COLOR_EXPORT_PATH,
  ...VEHICLE_DETAIL_EXPORT_PATHS,
];

function getRelocationOrderTransportReasonLabel(order: RelocationOrder): string {
  if (!order.transportReason) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const label = i18next.t(RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS[order.transportReason]);

  return label ?? order.transportReason;
}

function getRelocationOrderOrderedByLabel(order: RelocationOrder): string {
  const orderedBy = order.orderedBy;

  if (!orderedBy) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  return getFullUserName({ user: orderedBy });
}

function getRelocationOrderTransportCompanyLabel(order: RelocationOrder): string {
  if (order.transportMethod !== RelocationOrderTransportMethod.TransportCompany) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  return order.transportCompany?.name ?? i18next.t("fit:NoTransportCompanyAssignedYet");
}

function OrderInformationField({ label, value }: { label: string; value: ReactElement | string }) {
  return (
    <div className="space-y-0.5">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function CompactOrderInformationCell({ row }: { row: RowData }) {
  const order = row.original;
  const shouldShowTransportCompany =
    order.transportMethod === RelocationOrderTransportMethod.TransportCompany || Boolean(order.transportCompany?.name);

  return (
    <div className="flex min-w-0 flex-col gap-2 py-1">
      <OrderInformationField
        label={i18next.t("fit:TransportReason")}
        value={getRelocationOrderTransportReasonLabel(order)}
      />
      <OrderInformationField
        label={i18next.t("fit:TransportMethod")}
        value={getRelocationOrderTransportMethodLabel(order.transportMethod)}
      />
      {shouldShowTransportCompany ? (
        <OrderInformationField
          label={i18next.t("fit:TransportCompany")}
          value={getRelocationOrderTransportCompanyLabel(order)}
        />
      ) : null}
      <OrderInformationField
        label={i18next.t("fit:RelocationOrderOrderedBy")}
        value={getRelocationOrderOrderedByLabel(order)}
      />
    </div>
  );
}

const widescreenColumns = {
  columnBrand: {
    id: "brand",
    size: 50,
    align: "center",
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Brand"),
    exportPaths: [VEHICLE_BRAND_EXPORT_PATH, ...VEHICLE_DETAIL_EXPORT_PATHS],
    cell: ({ row }) => {
      const brand = row.original.vehicle?.modelGeneration?.modelFamily?.brand;
      if (!brand) return <EmptyValue />;
      return (
        <div className="flex flex-col items-center gap-1">
          <BrandLogo brand={brand} />
          <span className="w-full truncate text-center text-xs">{brand.name}</span>
        </div>
      );
    },
  },
  columnModelGenerationName: {
    id: "modelGeneration",
    size: 50,
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Model"),
    exportPaths: [VEHICLE_MODEL_GENERATION_EXPORT_PATH],
    cell: ({ row }) => {
      const modelGenerationName = row.original.vehicle?.modelGeneration?.name;
      if (!modelGenerationName) return <EmptyValue />;

      const vehicleBlock = row.original.vehicle?.vehicleBlock;
      const isBlocked = vehicleBlock !== null && vehicleBlock !== undefined;

      if (!isBlocked) {
        return modelGenerationName;
      }

      const reason = vehicleBlock.reason;
      const reasonLabel = VEHICLE_BLOCK_REASONS[reason];

      const displayLabel = reasonLabel ? i18next.t(reasonLabel) : reason;
      const blockedUntil = vehicleBlock.blockedUntil;
      const formattedDate = blockedUntil ? formatDateToLocale(blockedUntil, { dateOnly: true }) : null;

      return (
        <div className="flex items-center gap-2">
          <span>{modelGenerationName}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <LockIcon className="text-muted-foreground h-4 w-4" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>
                  {i18next.t("fit:Blocked")}: {displayLabel}
                  {formattedDate && ` bis ${formattedDate}`}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  columnVin: {
    id: "vin",
    size: 50,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:VIN"),
    exportPaths: [VEHICLE_VIN_EXPORT_PATH],
    cell: ({ row }) => row.original.vehicle?.vin || <EmptyValue />,
  },
  columnColor: {
    id: "color",
    size: 50,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Color"),
    exportPaths: [VEHICLE_COLOR_EXPORT_PATH],
    cell: ({ row }) => automaticCellRenderer(row.original.vehicle?.color),
  },
  columnTransportReason: {
    id: "transportReason",
    size: 80,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:TransportReason"),
    accessorFn: (row) => row.transportReason,
    cell: ({ row }) => {
      const transportReason = row.original.transportReason;
      if (!transportReason) return <EmptyValue />;

      const label = i18next.t(RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS[transportReason]);
      return label ?? transportReason;
    },
  },
  columnTransportMethod: {
    id: "transportMethod",
    size: 80,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:TransportMethod"),
    accessorFn: (row) => row.transportMethod,
    cell: ({ row }) => getRelocationOrderTransportMethodLabel(row.original.transportMethod),
  },
  columnTransportCompany: {
    id: "transportCompany",
    size: 120,
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:TransportCompany"),
    cell: ({ row }) => {
      const label = getRelocationOrderTransportCompanyLabel(row.original);

      return label === EMPTY_VALUE_PLACEHOLDER ? <EmptyValue /> : label;
    },
  },
  columnOrderedBy: {
    id: "orderedBy",
    size: 120,
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:RelocationOrderOrderedBy"),
    cell: ({ row }) => {
      const label = getRelocationOrderOrderedByLabel(row.original);

      return label === EMPTY_VALUE_PLACEHOLDER ? <EmptyValue /> : <span>{label}</span>;
    },
  },
};

const desktopColumns = {
  columnVehicleInfoWithBrand: {
    id: "vehicleInfoWithBrand",
    size: 150,
    enableSorting: false,
    visibility: [DeviceType.Desktop],
    header: () => i18next.t("common:Vehicle"),
    exportPaths: VEHICLE_EXPORT_PATHS,
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      return (
        <div className="flex items-center gap-3">
          {brand && <BrandLogo brand={brand} />}
          <div className="flex min-w-0 flex-col gap-1">
            {(brandName || modelGenerationName) && (
              <div className="truncate font-medium">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
            )}
            <div className="text-muted-foreground text-sm">
              <div className="font-mono text-xs">{vehicle.vin}</div>
              <div>{vehicle.color}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  columnVehicleInfoWithBrandNoColor: {
    id: "vehicleInfoWithBrandNoColor",
    size: 150,
    enableSorting: false,
    visibility: [DeviceType.Desktop],
    header: () => i18next.t("common:Vehicle"),
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      return (
        <div className="flex items-center gap-3">
          {brand && <BrandLogo brand={brand} />}
          <div className="flex min-w-0 flex-col gap-1">
            {(brandName || modelGenerationName) && (
              <div className="truncate font-medium">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
            )}
            <div className="text-muted-foreground text-sm">
              <div className="font-mono text-xs">{vehicle.vin}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  columnTransportMethod: {
    id: "transportMethod",
    size: 120,
    enableSorting: true,
    visibility: [DeviceType.Desktop],
    header: () => i18next.t("fit:TransportMethod"),
    accessorFn: (row) => row.transportMethod,
    cell: ({ row }) => getRelocationOrderTransportMethodLabel(row.original.transportMethod),
  },
  columnOrderInformation: {
    id: "orderInformation",
    size: 260,
    enableSorting: false,
    visibility: [DeviceType.Desktop],
    header: () => i18next.t("fit:OrderInformation"),
    cell: CompactOrderInformationCell,
  },
};

const createRouteWithStatusCell = (userType?: FitUserType): (({ row }: { row: RowData }) => ReactElement) => {
  return function RouteWithStatusCell({ row }: { row: RowData }) {
    const order = row.original;
    const status = order.status as RelocationOrderStatus;

    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm">
          {getRelocationOrderRoutePoints(order).map((point) =>
            renderRelocationOrderRoutePoint(point, {
              rowClassName: "flex items-center gap-1",
              labelClassName: "text-muted-foreground",
              valueClassName: "font-medium",
              currentMarkerClassName: "text-primary/80 italic",
            }),
          )}
        </div>
        <div>
          {renderRelocationOrderStatus({
            status: status,
            variant: RelocationOrderStatusContainerVariant.COMPACT,
            userType,
            transportMethod: order.transportMethod,
          })}
        </div>
      </div>
    );
  };
};

const widescreenAndDesktopColumns = {
  columnInternalRouteWithStatus: {
    id: "routeWithStatus",
    size: 400,
    enableSorting: false,
    visibility: [DeviceType.Widescreen, DeviceType.Desktop],
    header: () => i18next.t("fit:RouteAndStatus"),
    cell: createRouteWithStatusCell(),
  },
  columnExternalRouteWithStatus: {
    id: "routeWithStatus",
    size: 400,
    enableSorting: false,
    visibility: [DeviceType.Widescreen, DeviceType.Desktop],
    header: () => i18next.t("fit:RouteAndStatus"),
    cell: createRouteWithStatusCell(FitUserType.EXTERNAL),
    exportPaths: ROUTE_WITH_STATUS_EXPORT_PATHS,
  },
  columnEarliestPickupDate: {
    id: "earliestPickupDate",
    size: 200,
    enableSorting: true,
    visibility: [DeviceType.Widescreen, DeviceType.Desktop],
    header: () => i18next.t("fit:PickupFrom"),
    accessorFn: (row) => row.earliestPickupDate,
    cell: ({ row }) => renderPickupDate(row.original.earliestPickupDate, row.original.status),
  },
};

const createCompactRouteCell = (userType?: FitUserType): (({ row }: { row: RowData }) => ReactElement) => {
  return function CompactRouteCell({ row }: { row: RowData }) {
    const order = row.original;
    const status = order.status as RelocationOrderStatus;

    return (
      <div className="flex flex-col gap-1 text-sm">
        {getRelocationOrderRoutePoints(order).map((point) =>
          renderRelocationOrderRoutePoint(point, {
            rowClassName: "flex min-w-0 items-center gap-1",
            labelClassName: "text-muted-foreground shrink-0",
            valueClassName: "truncate font-medium",
            currentMarkerClassName: "text-primary/80 shrink-0 italic",
          }),
        )}
        <div className="flex min-w-0 items-center gap-1">
          <span className="text-muted-foreground shrink-0">{i18next.t("fit:Pickup")}:</span>
          {renderPickupDate(order.earliestPickupDate, order.status)}
        </div>
        <div className="mt-1">
          {renderRelocationOrderStatus({
            status: status,
            variant: RelocationOrderStatusContainerVariant.COMPACT,
            userType,
            transportMethod: order.transportMethod,
          })}
        </div>
      </div>
    );
  };
};

const tabletColumns = {
  columnCompactVehicleInfo: {
    id: "compactVehicleInfo",
    size: 300,
    enableSorting: false,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("common:Vehicle"),
    exportPaths: VEHICLE_EXPORT_PATHS,
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      return (
        <div className="flex flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2 font-medium">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelGenerationName) && (
              <span className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground text-sm">
            <div className="truncate font-mono text-xs">{vehicle.vin}</div>
            <div className="truncate">{vehicle.color}</div>
          </div>
        </div>
      );
    },
  },
  columnCompactVehicleInfoNoColor: {
    id: "compactVehicleInfoNoColor",
    size: 300,
    enableSorting: false,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("common:Vehicle"),
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      return (
        <div className="flex flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2 font-medium">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelGenerationName) && (
              <span className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground text-sm">
            <div className="truncate font-mono text-xs">{vehicle.vin}</div>
          </div>
        </div>
      );
    },
  },
  columnTransportMethod: {
    id: "transportMethod",
    size: 140,
    enableSorting: true,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("fit:TransportMethod"),
    accessorFn: (row) => row.transportMethod,
    cell: ({ row }) => getRelocationOrderTransportMethodLabel(row.original.transportMethod),
  },
  columnInternalCompactRoute: {
    id: "compactRoute",
    size: 400,
    enableSorting: false,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("fit:RouteAndStatus"),
    cell: createCompactRouteCell(),
  },
  columnExternalCompactRoute: {
    id: "compactRoute",
    size: 400,
    enableSorting: false,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("fit:RouteAndStatus"),
    cell: createCompactRouteCell(FitUserType.EXTERNAL),
    exportPaths: [...ROUTE_WITH_STATUS_EXPORT_PATHS, "earliestPickupDate"],
  },
  columnOrderInformation: {
    id: "orderInformation",
    size: 280,
    enableSorting: false,
    visibility: [DeviceType.Tablet],
    header: () => i18next.t("fit:OrderInformation"),
    cell: CompactOrderInformationCell,
  },
};

const mobileColumns = {
  columnMobileComplete: {
    id: "mobileComplete",
    size: 1000,
    enableSorting: false,
    visibility: [DeviceType.Mobile],
    header: () => i18next.t("fit:VehicleAndRoute"),
    exportPaths: [...VEHICLE_EXPORT_PATHS, ...ROUTE_WITH_STATUS_EXPORT_PATHS, "earliestPickupDate"],
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      const order = row.original;
      const status = order.status as RelocationOrderStatus;
      return (
        <div className="flex flex-col gap-2 py-2 text-left">
          <div className="flex min-w-0 items-center gap-2 text-base font-semibold">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelGenerationName) && (
              <span className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:VIN")}:</span>
              <span className="truncate font-mono text-xs">{vehicle.vin}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Color")}:</span>
              <span className="truncate">{vehicle.color}</span>
            </div>
            {getRelocationOrderRoutePoints(order).map((point) =>
              renderRelocationOrderRoutePoint(point, {
                rowClassName: "flex min-w-0 items-center gap-2",
                labelClassName: "shrink-0 font-medium",
                valueClassName: "text-foreground truncate font-medium",
                currentMarkerClassName: "text-primary/80 shrink-0 italic",
              }),
            )}
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:PickupFrom")}:</span>
              {renderPickupDate(order.earliestPickupDate, order.status)}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Status")}:</span>
              <div className="min-w-0">
                {renderRelocationOrderStatus({
                  status: status,
                  variant: RelocationOrderStatusContainerVariant.COMPACT,
                  transportMethod: order.transportMethod,
                })}
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
  columnMobileCompleteWithTransportMethodNoColor: {
    id: "mobileCompleteWithTransportMethodNoColor",
    size: 1000,
    enableSorting: false,
    visibility: [DeviceType.Mobile],
    header: () => i18next.t("fit:VehicleAndRoute"),
    cell: ({ row }) => {
      const vehicle = row.original.vehicle;
      if (!vehicle) return <EmptyValue />;

      const brand = vehicle?.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelGenerationName = vehicle?.modelGeneration?.name;
      const order = row.original;
      const status = order.status as RelocationOrderStatus;
      return (
        <div className="flex flex-col gap-2 py-2 text-left">
          <div className="flex min-w-0 items-center gap-2 text-base font-semibold">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelGenerationName) && (
              <span className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:VIN")}:</span>
              <span className="truncate font-mono text-xs">{vehicle.vin}</span>
            </div>
            {getRelocationOrderRoutePoints(order).map((point) =>
              renderRelocationOrderRoutePoint(point, {
                rowClassName: "flex min-w-0 items-center gap-2",
                labelClassName: "shrink-0 font-medium",
                valueClassName: "text-foreground truncate font-medium",
                currentMarkerClassName: "text-primary/80 shrink-0 italic",
              }),
            )}
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:TransportMethod")}:</span>
              <span className="text-foreground truncate font-medium">
                {getRelocationOrderTransportMethodLabel(order.transportMethod)}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:PickupFrom")}:</span>
              {renderPickupDate(order.earliestPickupDate, order.status)}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Status")}:</span>
              <div className="min-w-0">
                {renderRelocationOrderStatus({
                  status: status,
                  variant: RelocationOrderStatusContainerVariant.COMPACT,
                  transportMethod: order.transportMethod,
                })}
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
  columnMobileOrderInformation: {
    id: "mobileOrderInformation",
    size: 1000,
    enableSorting: false,
    visibility: [DeviceType.Mobile],
    header: () => i18next.t("fit:OrderInformation"),
    cell: ({ row }) => {
      const order = row.original;
      const status = order.status as RelocationOrderStatus;

      return (
        <div className="flex flex-col gap-3 py-2 text-left">
          <CompactOrderInformationCell row={row} />

          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            {getRelocationOrderRoutePoints(order).map((point) =>
              renderRelocationOrderRoutePoint(point, {
                rowClassName: "flex min-w-0 items-center gap-2",
                labelClassName: "shrink-0 font-medium",
                valueClassName: "text-foreground truncate font-medium",
                currentMarkerClassName: "text-primary/80 shrink-0 italic",
              }),
            )}
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:PickupFrom")}:</span>
              {renderPickupDate(order.earliestPickupDate, order.status)}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Status")}:</span>
              <div className="min-w-0">
                {renderRelocationOrderStatus({
                  status: status,
                  variant: RelocationOrderStatusContainerVariant.COMPACT,
                  transportMethod: order.transportMethod,
                })}
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
};

export const relocationTableOrderDefaultColumns = {
  widescreen: widescreenColumns,
  desktop: desktopColumns,
  widescreenAndDesktop: widescreenAndDesktopColumns,
  tablet: tabletColumns,
  mobile: mobileColumns,
};
